

/**
 * Format a Date into YYYY-MM-DD (local date, zero-padded).
 */
export const formatDateYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** * Format a Date or date string into DD-MM-YYYY (local date, zero-padded). */
export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const d2 = typeof date === 'string' ? new Date(date.includes('T') ? date : date + 'T00:00:00') : date;
  if (isNaN(d2.getTime())) return '';
  const y = d2.getFullYear();
  const m = String(d2.getMonth() + 1).padStart(2, '0');
  const dd = String(d2.getDate()).padStart(2, '0');
  return `${dd}-${m}-${y}`;
};
/**  Get the current year. */
export const getCurrentYear = (): number => new Date().getFullYear();

/** * Get today's date in YYYY-MM-DD format. * Convenience wrapper for formatDateYMD(new Date()) */
export const getTodayYMD = (): string => formatDateYMD(new Date());

/**
 * Format a Date into DD/MM/YYYY format (en-GB locale).
 * @param date - Date object or date string
 * @returns Formatted date string in DD/MM/YYYY format
 * 
 * NOTE: For YYYY-MM-DD strings, we parse as local date to avoid
 * timezone shift issues (JS Date parses ISO strings as UTC).
 */
export const formatDateDMY = (date?: Date | string | null): string => {
  if (!date) return new Date().toLocaleDateString('en-GB');
  
  let d: Date;
  if (typeof date === 'string') {
    // Check if string is in YYYY-MM-DD format to parse as local date
    const ymdMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      // Parse as local date to avoid UTC timezone shift
      d = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB');
};

// Allow only future dates for next_visit_date (min = tomorrow)
/** * Returns the ISO date string (YYYY-MM-DD) for tomorrow, based on the given date. */
export const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatDateYMD(d);
};
/** * Returns the ISO date string (YYYY-MM-DD) for today. */
export const todayISO = () => {
  return formatDateYMD(new Date());
};

/**
 * Returns the ISO date string (YYYY-MM-DD) for a date N days from today.
 * @param days - Number of days to add (can be negative for past dates)
 * @returns Date string in YYYY-MM-DD format
 */
export const getDateFromToday = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatDateYMD(date);
};


// util: check if a YYYY-MM-DD string is strictly greater than yesterday
export const isAfterYesterday = (dateStr: string | undefined | null): boolean => {
  if (!dateStr) return false;
  const selected = new Date(dateStr);
  if (Number.isNaN(selected.getTime())) return false;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const selectedISO = selected.toISOString().split('T')[0];
  const yesterdayISO = yesterday.toISOString().split('T')[0];

  return selectedISO > yesterdayISO;
};

export function formatMonthYear(dateStr: string | null | undefined, locale: string = 'en-US'): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return ''; // Return empty string for invalid dates to allow fallback

  return date.toLocaleString(locale, {
    month: 'long',
    year: 'numeric',
  });
}
/** * Convert date to ISO format with current time * If no date provided, returns current datetime * If date is YYYY-MM-DD format, appends current time */
export const toISODateString = (dateStr?: string): string => {
  const now = new Date();
  
  if (!dateStr) {
    return now.toISOString();
  }
  // If already ISO format, return as-is
  if (dateStr.includes('T')) {
    return dateStr;
  }
  // Convert YYYY-MM-DD to ISO format with current time
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return new Date(`${dateStr}T${hours}:${minutes}:${seconds}Z`).toISOString();
};

/** * Format a date/time string into DD/MM/YYYY, HH:mm format. */
export const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a date string into DD MMM YYYY format (e.g., "17 Dec 2025").
 */
export const formatDateShortMonth = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format an ISO date string into DD-MMM-YYYY hh:mm am/pm format.
 * @param iso - ISO date string
 * @returns Formatted string like "17-Dec-2025 09:30 am", or '-' if invalid
 */
export const formatDateTimeAmPm = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const yyyy = d.getFullYear();
  const hours12 = d.getHours() % 12 || 12;
  const hh = String(hours12).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'pm' : 'am';
  return `${dd}-${month}-${yyyy} ${hh}:${mi} ${ampm}`;
};

/**
 * Calculate a date offset by a specified number of days from a given date.
 * @param baseDate - The base date string (YYYY-MM-DD format) or null
 * @param daysOffset - Number of days to add (positive) or subtract (negative)
 * @returns The calculated date in YYYY-MM-DD format, or empty string if baseDate is null/undefined
 */
export const getDateWithOffset = (baseDate: string | null | undefined, daysOffset: number): string => {
  if (!baseDate) return '';
  const date = new Date(baseDate);
  return new Date(date.getTime() + daysOffset * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
};

/**
 * Format a time string (HH:mm:ss or HH:mm) into 12-hour format (e.g., "09:00 AM").
 * @param timeStr - Time string in HH:mm or HH:mm:ss format
 * @returns Formatted time string in 12-hour format, or empty string if invalid
 */
export const formatTime12Hour = (timeStr: string | null | undefined): string => {
  if (!timeStr) return '';
  
  const [h, m = '00'] = timeStr.split(':');
  const hour = +h;
  
  if (isNaN(hour)) return '';
  
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = (hour % 12 || 12).toString().padStart(2, '0');
  
  return `${hour12}:${m.padStart(2, '0')} ${period}`;
};

/**
 * Extract and format time from an ISO datetime string into 12-hour format (e.g., "12:19 pm").
 * @param isoStr - ISO datetime string (e.g., "2026-03-17T12:19:00Z")
 * @returns Formatted time string, or empty string if invalid/null
 */
export const formatTimeFromISO = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
};
